import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { LogOut, ChevronLeft, KeyRound } from 'lucide-react';
import logoImg from 'src/assets/images/ahedge_icon_blue_inside.png';
import { Badge } from 'src/components/ui/badge';
import { useAuthActions } from 'src/hooks/useAuthActions';
import { getNavigation } from 'src/config/Navigation';
import {
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarSeparator,
} from 'src/components/ui/sidebar';
import { useAuth } from 'src/hooks/useAuth';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function AppSidebar() {
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { logout } = useAuthActions();
  const { open, toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const { getUserInfo, isOnlyRole } = useAuth();
  const user = getUserInfo();

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const userString = localStorage.getItem('user');
      const parsedUser = userString ? JSON.parse(userString) : null;
      try {
        setUserData(parsedUser);
        setIsAuthenticated(!!token);
      } catch (error) {
        console.error('Error parsing user data:', error);
        setUserData(null);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, [location]);

  const navigationItems = getNavigation(user?.roles || []);

  const handleLogout = () => {
    logout();
  };

  const initials = (() => {
    if (!userData) return 'U';
    const first = userData?.firstName?.charAt(0) || '';
    const last = userData?.lastName?.charAt(0) || '';
    return (
      (first + last).toUpperCase() ||
      userData?.email?.charAt(0)?.toUpperCase() ||
      'U'
    );
  })();

  const isUrlActive = (urlParam) => {
    const urls = typeof urlParam === 'string' ? [urlParam] : urlParam;
    return urls.some((url) => location.pathname.startsWith(url));
  };

  const renderMenuItem = (item) => {
    const isMainMenuActive = isUrlActive(item.href);
    const isDisabled = !!item.comingSoon;

    return (
      <SidebarMenuItem key={item.name} className="relative mb-px">
        {isMainMenuActive && (
          <div className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[18px] bg-primary rounded-r-[3px] z-10" />
        )}
        <SidebarMenuButton
          asChild={!isDisabled}
          disabled={isDisabled}
          tooltip={{
            children: isDisabled ? `${item.name} – Coming Soon` : item.name,
            className:
              'bg-foreground-strong text-white text-[12px] font-[500] px-[10px] py-[5px] rounded-[6px] shadow-[0_2px_8px_rgba(0,0,0,0.15)] border-none tracking-normal',
          }}
          className={classNames(
            'gap-[11px] py-[9px] px-3 rounded-lg text-[13.5px] h-10',
            isMainMenuActive
              ? 'bg-white/[0.14] text-white font-[550]'
              : 'text-white/60 font-[450] hover:bg-white/[0.08] hover:text-white/90'
          )}
        >
          {isDisabled ? (
            <>
              <item.icon
                className={classNames(
                  'h-[18px] w-[18px] shrink-0',
                  isMainMenuActive ? 'opacity-100' : 'opacity-70'
                )}
              />
              {open && <span className="truncate">{item.name}</span>}
              {open && (
                <Badge
                  variant="simple"
                  className={[
                    'text-[9px] font-semibold uppercase tracking-[.04em]',
                    'px-[6px] py-[2px] rounded-[4px] leading-[1.4] whitespace-nowrap',
                    'bg-white/[0.18] text-white/70',
                    'ml-auto shrink-0 border-none',
                  ].join(' ')}
                >
                  Soon
                </Badge>
              )}
            </>
          ) : (
            <Link to={Array.isArray(item.href) ? item.href[0] : item.href}>
              <item.icon
                className={classNames(
                  'h-[18px] w-[18px] shrink-0',
                  isMainMenuActive ? 'opacity-100' : 'opacity-70'
                )}
              />
              {open && <span className="truncate">{item.name}</span>}
            </Link>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  const isAdminOnly = isOnlyRole('ahedge_admin');

  return (
    <Sidebar
      collapsible="icon"
      className="p-0"
      style={{
        background: 'var(--sidebar-gradient)',
        '--sidebar-background': '0 0% 0% / 0',
        '--sidebar-foreground': '0 0% 100%',
        '--sidebar-accent': '0 0% 100% / 0.08',
        '--sidebar-accent-foreground': '0 0% 100% / 0.85',
        '--sidebar-border': '0 0% 100% / 0.1',
      }}
    >
      {/* Edge-mounted collapse toggle */}
      <button
        type="button"
        onClick={toggleSidebar}
        aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
        className={classNames(
          'absolute top-[14px] -right-3 z-[50]',
          'w-6 h-6 min-w-6 rounded-full',
          'flex items-center justify-center',
          'border cursor-pointer',
          'shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
          'transition-[opacity,color,background-color,border-color,box-shadow] duration-300',
          'focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2',
          open
            ? 'opacity-0 group-hover:opacity-100 bg-card border-input text-muted-foreground hover:bg-brand-bg-subtle dark:hover:bg-brand-bg-subtle hover:text-primary hover:border-[rgba(32, 104, 178,0.22)] hover:shadow-[0_2px_6px_rgba(32, 104, 178,0.14)]'
            : 'opacity-100 bg-brand-bg-subtle text-primary border-[rgba(32, 104, 178,0.2)] hover:bg-primary hover:text-white hover:border-primary hover:shadow-[0_2px_8px_rgba(32, 104, 178,0.25)]'
        )}
      >
        <ChevronLeft
          className={classNames(
            'transition-transform duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
            !open && 'rotate-180'
          )}
          style={{ width: 13, height: 13, strokeWidth: 2 }}
        />
      </button>

      {/* Header */}
      <div className="p-0 border-b border-white/10">
        <div className="h-full items-center">
          <div className="w-full">
            <div className="flex items-center justify-start">
              <Link
                to="/opportunities"
                className={`flex items-center gap-2 py-[1.6rem] ${open ? 'px-4' : 'px-3'} !h-12`}
              >
                <img
                  src={logoImg}
                  alt="Ahedge Logo"
                  className="h-6 w-auto shrink-0"
                />
                {open && (
                  <span className="text-xl font-semibold text-white">
                    AHEDGE
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <SidebarContent className="p-2 scrollbar-sidebar">
        {/* Staffing section – hidden for ahedge_admin-only users */}
        {!isAdminOnly && (
          <SidebarGroup>
            {open && (
              <SidebarGroupLabel className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-white/30 px-[10px] pt-4 pb-[7px]">
                Staffing
              </SidebarGroupLabel>
            )}
            <SidebarMenu>
              {navigationItems.Staffing?.map(renderMenuItem)}
            </SidebarMenu>
          </SidebarGroup>
        )}

        {/* Settings – visible to any role with at least one Settings item */}
        {navigationItems.Settings?.length > 0 && (
          <>
            {!isAdminOnly && <SidebarSeparator className="bg-white/10" />}
            <SidebarGroup>
              {open && (
                <SidebarGroupLabel className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-white/30 px-[10px] pt-4 pb-[7px]">
                  Settings
                </SidebarGroupLabel>
              )}
              <SidebarMenu>
                {navigationItems.Settings?.map(renderMenuItem)}
              </SidebarMenu>
            </SidebarGroup>
          </>
        )}

        {/* User Management – admin only */}
        {user?.roles?.includes('ahedge_admin') && (
          <>
            <SidebarSeparator className="bg-white/10" />
            <SidebarGroup>
              {open && (
                <SidebarGroupLabel className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-white/30 px-[10px] pt-4 pb-[7px]">
                  User Management
                </SidebarGroupLabel>
              )}
              <SidebarMenu>
                {navigationItems.UserManagement?.map(renderMenuItem)}
              </SidebarMenu>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>

      {/* User Profile Section */}
      {isAuthenticated && userData && (
        <div className="p-2 border-t border-white/10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={`w-full flex items-center gap-[10px] ${open ? 'px-3' : 'justify-center px-2'} py-[10px] rounded-lg transition-colors hover:bg-white/[0.06] outline-none cursor-pointer`}
              >
                <div className="w-[34px] h-[34px] min-w-[34px] rounded-full bg-white/[0.12] flex items-center justify-center text-[12px] font-semibold text-white flex-shrink-0">
                  {initials}
                </div>
                {open && (
                  <div className="flex flex-col text-left overflow-hidden flex-1 min-w-0">
                    <span className="text-[13px] font-semibold text-white leading-[1.3] truncate">
                      {userData?.firstName}
                    </span>
                    <span className="text-[11px] text-white/40 leading-[1.3] truncate">
                      {userData?.role || 'Staff'}
                    </span>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side={open ? 'top' : 'right'}
              align="start"
              className={`p-[6px] rounded-xl shadow-[0_10px_30px_rgba(0,0,0,.18),0_1px_4px_rgba(0,0,0,.08)] border-input bg-card ${open ? 'w-[--radix-popper-anchor-width]' : 'w-48'}`}
            >
              <DropdownMenuLabel className="flex items-center gap-[11px] px-[10px] pt-3 pb-[10px] mb-1 border-b border-border">
                <div className="w-9 h-9 min-w-[36px] rounded-full bg-gradient-to-br from-[#E9F1FA] to-[#D4E4F7] dark:from-brand-bg-subtle dark:to-brand-bg-subtle flex items-center justify-center text-[12px] font-bold text-primary flex-shrink-0">
                  {initials}
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-[13.5px] font-semibold text-foreground-strong truncate">
                    {userData?.firstName}
                  </span>
                  <span className="text-[11.5px] text-muted-foreground truncate">
                    {userData?.email}
                  </span>
                </div>
              </DropdownMenuLabel>

              <DropdownMenuItem
                className="flex items-center gap-[10px] px-3 py-[9px] text-[13px] font-[450] text-muted-foreground rounded-lg cursor-pointer hover:bg-brand-bg-subtle hover:text-foreground-strong focus:bg-brand-bg-subtle"
                onClick={() => navigate('/change-password')}
              >
                <KeyRound
                  className="w-4 h-4 text-muted-foreground flex-shrink-0"
                  strokeWidth={1.7}
                />
                Change Password
              </DropdownMenuItem>

              <DropdownMenuSeparator className="my-1 bg-border" />

              <DropdownMenuItem
                className="flex items-center gap-[10px] px-3 py-[9px] text-[13px] font-[450] text-muted-foreground rounded-lg cursor-pointer hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 focus:bg-red-50 dark:focus:bg-red-500/10 focus:text-red-600"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 flex-shrink-0" strokeWidth={1.7} />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </Sidebar>
  );
}

export default AppSidebar;
